import { ttdTemplateDocument } from './ttd-template'
import { z } from 'zod'
import {
  reportTemplateSummarySchema,
  standardPresentation,
  type BlockTemplateDocument,
  type SectionPresentation,
  type TemplateBlock,
  type TemplateRole,
} from './template'

/**
 * Reusable starting points, drawn from what enabled subscriptions in the legacy admin portal
 * actually render.
 *
 * Measured rather than guessed. Of 314 ENABLED subscriptions: 253 (81%) use the default rendering
 * — 206 whose `template` field is an empty array plus 47 set to `DEFAULT` — then `UTAH_TEMPLATE`
 * (8), `DELAWARE` (5), and `BCG_TARIFFS`, `OKLA-UHG` and `SUNRISE_PROJECT` (4 each), followed by
 * roughly thirty templates carrying one or two subscriptions apiece.
 *
 * Four starters cover 274 of those 314. The tail is deliberately not modelled: a template per
 * client is what produced 56 near-identical files in the first place, and the ones that genuinely
 * differ (`emerald`, `narrative-pilot`) have the custom article HTML escape hatch instead.
 *
 * Named for their shape, not their client. `BCG_TARIFFS` is absent on purpose — its stylesheet is
 * byte-identical to the default's, and it differs only by leaving optional blocks out, which is a
 * toggle rather than a template.
 *
 * These are documents, not rows: creating a template POSTs one of these as its `document`, so the
 * catalogue stays reviewable in code and nothing has to be seeded into a database per environment.
 */
export interface StarterTemplate {
  id: string
  name: string
  /** One line, shown next to the name. Says what the shape is, not which client asked for it. */
  description: string
  /** What it reproduces, for anyone comparing against the admin portal. */
  legacy: string
  /**
   * Set when the entry is ONE CLIENT'S design rather than a reusable shape.
   *
   * The distinction is load-bearing, not labelling. A shared shape takes its colour and typeface
   * from whichever account renders it, which is what lets one template serve every client. A
   * client design brings its own palette through `shell.accent`, so picking it for a different
   * account renders that other client's brand — visibly wrong, and not obviously anyone's mistake.
   *
   * So these are excluded from the bulk install and only ever chosen deliberately, by name.
   */
  client?: string
  /**
   * What the installed row IS to the system, beyond its name (CLI-838). `'standard'` marks the one
   * template every report without a template renders through, so installing it is what makes the
   * standard layout editable; the install writes it onto the row and the renderer resolves by it.
   */
  role?: TemplateRole
  /** Every starter is a block document; the install translates it to the HTML kind. */
  document: () => BlockTemplateDocument
}

/** The reusable shapes — everything the bulk install should create. */
export function sharedStarterTemplates(): StarterTemplate[] {
  return STARTER_TEMPLATES.filter((starter) => starter.client === undefined)
}

/** The library row the standard layout lives in once installed — by name here, by `role` in the database. */
export const STANDARD_TEMPLATE_NAME = 'Standard'

/**
 * The legacy default's own presentation, which every starter here begins from.
 *
 * `default/template.html` in the admin portal: Arial, `.header-name` bold and underlined,
 * `.sub-header-name` in #6c757d, `.article-snippets` a disc list, the `.full-text-link` "Read
 * More", and the syndication line. 48 of the 56 templates carry the full-text link and 47 use
 * disc snippets, which is why those are on by default here and not in `standardPresentation()` —
 * that one is the renderer's own neutral baseline, and it should stay that.
 */
/**
 * Legacy's term highlights, as the data API paints them for every report it serves
 * (clipbook-data-api news.service.ts: `principal` → #FBF719, `not-principal` → #cce0f5) and the
 * portal's own highlighter used for posts. A term match sat on light blue, a principal's name on
 * yellow; the Visit Galveston send of 2026-10-06 carried 57 light-blue spans and nothing else.
 */
export const LEGACY_HIGHLIGHT_COLOR = '#cce0f5'
export const LEGACY_PRINCIPAL_HIGHLIGHT_COLOR = '#fbf719'

/**
 * What Gmail renders the legacy default's unstyled markup at (CLI-838). The template sets no size
 * anywhere, so every size is the inbox's: Gmail's message body is 13px at a 1.5 line height, an
 * `<h2>` 1.5× that, an `<h3>` 1.17×, and `<h4>`, `<p>` and `<li>` the body size. Measured with
 * getComputedStyle on a real legacy send (Kevin Olickal, 2026-10-09) beside the Headline send Alex
 * had flagged as "bigger". Whole pixels because the model takes whole pixels: 19.5 → 20, 15.21 → 15.
 */
const LEGACY_BODY_PX = 13
const LEGACY_LINE_HEIGHT = 1.5
const LEGACY_H2_PX = 20
const LEGACY_H3_PX = 15

function pressClipsPresentation(): SectionPresentation {
  return {
    ...standardPresentation(),
    fields: { outlet: true, date: true, author: true, summary: false, readMore: true },
    snippets: {
      mode: 'matched',
      limit: 3,
      highlightTerms: true,
      style: 'bullets',
      highlightColor: LEGACY_HIGHLIGHT_COLOR,
      principalHighlightColor: LEGACY_PRINCIPAL_HIGHLIGHT_COLOR,
    },
    outletPlacement: 'beforeHeadline',
    styles: {
      headerName: { bold: true, underline: true },
      subHeaderName: { color: '#6c757d' },
      meta: { italic: true, indentPx: 40 },
      snippet: { indentPx: 80 },
    },
    stylesBySource: {
      // `.twitter-username { font-weight: bold }` and `.podcast-episode-title` — the two
      // per-source deviations nearly every legacy template shares.
      x: { headline: { bold: true } },
      podcast: { headline: { bold: true } },
    },
  }
}

function documentWith(
  presentation: SectionPresentation,
  options: { insights?: boolean; legacy?: boolean } = {},
): BlockTemplateDocument {
  // The legacy portal's own words when a subscription set none (`constants/contants.js`): every
  // one of these shapes was sent with them, so a starter that said something else would be the
  // first visible difference a client noticed. A report's own greeting and message override these
  // per report (`settings.prose`), as the subscription's did.
  const greeting: TemplateBlock = {
    id: 'greeting',
    type: 'greeting',
    enabled: true,
    content: '<p>Good morning,</p>',
  }
  const body: TemplateBlock = {
    id: 'body',
    type: 'richText',
    enabled: true,
    content: '<p>Sharing press clips for today.</p>',
  }
  // Roughly a quarter of enabled subscriptions turn insights on, so it ships present and OFF
  // rather than absent: a block someone can enable beats one they have to know to add.
  const insights: TemplateBlock = {
    id: 'insights',
    type: 'insights',
    enabled: options.insights ?? false,
    heading: 'Insights',
    style: 'plain',
    showCitations: true,
  }
  const sections: TemplateBlock = {
    id: 'sections',
    type: 'sections',
    enabled: true,
    default: presentation,
  }
  const signature: TemplateBlock = {
    id: 'signature',
    type: 'signature',
    enabled: true,
    source: 'custom',
    closing: 'Best,',
    // The legacy sign-off is the sender's name alone — "Clipbook Team" for the team@ sender.
    content: '<p><span data-merge-field="sender_name">Sender name</span></p>',
  }
  if (options.legacy) {
    // `default/template.html` as it is actually sent: no card, no masthead, the sign-off ABOVE
    // the coverage, Arial on white. What a client on the default has been receiving for years.
    return {
      version: 2,
      target: 'email',
      masthead: { text: '', showLogo: false },
      shell: {
        showMasthead: false,
        framed: false,
        pageBackground: '#ffffff',
        cardBackground: '#ffffff',
        // The legacy default has no container of any kind — `<body>` straight into the content —
        // so its text runs the width of the pane. An 800px card centred in a wider pane was the
        // first difference a client saw (CLI-838). What it DOES keep is the browser's own 8px
        // `<body>` margin, which the template never zeroed: Gmail strips it, but every other
        // client and every preview shows it, and without it the email sits hard against the
        // edge — the second difference Kaushik saw, on the side-by-side.
        fullWidth: true,
        outerPaddingPx: 8,
        bodyPaddingPx: 0,
        // `<body style="font-family: Arial, sans-serif">`, black — the same for every client,
        // whatever their brand, so the legacy default names its master font and colour.
        font: 'arial',
        textColor: '#000000',
        // No font size or line height is set anywhere in the legacy default, so its prose is
        // whatever the inbox gives unstyled text — and in Gmail, where the clients read it, that is
        // 13px at 1.5 (measured on a real send, 2026-10-09), not the browser's 16px "normal" this
        // once assumed. Everything below is sized against the same measurement.
        prose: { sizePx: LEGACY_BODY_PX, lineHeight: LEGACY_LINE_HEIGHT },
      },
      blocks: [greeting, body, signature, insights, sections],
    }
  }
  return {
    version: 2,
    target: 'email',
    masthead: { text: '{{report_title}}', showLogo: true },
    blocks: [greeting, body, insights, sections, signature],
  }
}

/**
 * The legacy default's article, byte for byte where it matters: `<h3>` with the outlet bolded
 * ahead of a plain link, the licensed "Read More" as a grey pill on the same line, the date long
 * and with the time, "| By:" before the author, the snippets a disc list, and the syndicated
 * outlets as the list's last item with each name linked to its copy. Reachable only as a custom
 * row — the standard row's controls cover fields and colours, not this composition. Margins are
 * the browser's `1em` at each size, which is what the legacy markup had.
 */
const LEGACY_NEWS_ROW = `<h3 style="margin:${LEGACY_H3_PX}px 0 0 0;font-size:${LEGACY_H3_PX}px;line-height:${LEGACY_LINE_HEIGHT};font-weight:bold"><b>{{#if is_opinion}}[Opinion] - {{/if}}{{outlet}}&nbsp;</b><a href="{{url}}">{{headline}}</a>{{#if full_text_url}} <span style="background-color:#a9a9a9;border-radius:3px;padding:2px;margin-left:5px"><a href="{{full_text_url}}">Read More</a></span>{{/if}}</h3>
<p style="margin:0;font-size:${LEGACY_BODY_PX}px">{{date_long}}{{#if author}}&nbsp;|&nbsp; By: {{author}}{{/if}}</p>
<ul style="list-style-type:disc;margin:${LEGACY_BODY_PX}px 0;padding-left:40px;font-size:${LEGACY_BODY_PX}px">{{snippet_items}}{{#if syndicated_links}}<li><b>Syndicated By:</b>&nbsp;&nbsp;{{syndicated_links}}</li>{{/if}}</ul>`

/** The default's post: the account bold, the handle, the text as the link. */
/**
 * Legacy's DEFAULT template for a post (`.twitter-title` / `.twitter-text`): the author linked and
 * bold with the handle and the network in brackets, a date line, then the post's words as a plain
 * paragraph — the text was never the link. Browser-default `<p>` spacing, as the template had.
 */
const LEGACY_X_ROW = `<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px"><b><a href="{{url}}">{{outlet}}</a>{{#if handle}} ({{handle}}){{/if}} [X]</b></p>
<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px">{{date_long}}</p>
<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px">{{headline}}</p>`
const LEGACY_LINKEDIN_ROW = `<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px"><b><a href="{{url}}">{{outlet}} </a></b> [LinkedIn]</p>
<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px">{{date_long}}</p>
<p style="margin:${LEGACY_BODY_PX}px 0 0 0;font-size:${LEGACY_BODY_PX}px">{{headline}}</p>`

/**
 * The legacy default's presentation, as sent: the header an underlined black `<h2>` in title
 * case, its subtitle a grey `<h4>`, no rule beneath, and the rows above.
 */
function legacyDefaultPresentation(): SectionPresentation {
  const presentation = pressClipsPresentation()
  return {
    ...presentation,
    header: { show: true, rule: false },
    // Legacy's `{{#notEmptyHeader}}`: a header with nothing in it was not there.
    showEmptySections: false,
    styles: {
      // `<h2 class="header-name">` and `<h4 class="sub-header-name">`, at what Gmail gives those tags.
      headerName: {
        sizePx: LEGACY_H2_PX,
        bold: true,
        underline: true,
        color: '#000000',
        uppercase: false,
        letterSpacingPx: 0,
      },
      subHeaderName: { sizePx: LEGACY_BODY_PX, bold: true, color: '#6c757d' },
      // The `<h3>` legacy put over each group of posts: bold, black, an h3's size.
      sourceHeading: {
        sizePx: LEGACY_H3_PX,
        bold: true,
        color: '#000000',
        uppercase: false,
        letterSpacingPx: 0,
      },
    },
    // Legacy's default template headed every non-news group of a header whenever it had one —
    // `<h3>LinkedIn Posts:</h3>`, `<h3>Twitter Mentions:</h3>`, underlined `Broadcast Mentions`
    // and `Podcast Episodes` — and never the news, which simply came first. `sources` is that
    // list; the news and newsletters stay unheaded.
    sourceHeadings: {
      show: true,
      onlyWhenMixed: false,
      sources: ['x', 'linkedin', 'broadcast', 'podcast'],
      labels: {
        x: 'Twitter Mentions:',
        linkedin: 'LinkedIn Posts:',
        broadcast: 'Broadcast Mentions',
        podcast: 'Podcast Episodes',
      },
      rule: false,
    },
    articleHtml: { base: LEGACY_NEWS_ROW, x: LEGACY_X_ROW, linkedin: LEGACY_LINKEDIN_ROW },
  }
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'standard',
    name: STANDARD_TEMPLATE_NAME,
    description:
      'The standard layout — what every report without a template renders. Editing it changes all of them.',
    legacy: 'default — 253 of 314 enabled subscriptions',
    // Was "Press clips", a plain starter that happened to be byte-identical to the standard layout.
    // Naming it for what it is, and marking it, is what lets the library show and edit the layout
    // most reports actually send (CLI-838). A library that installed "Press clips" earlier keeps
    // that row as an ordinary template; this one is created alongside it.
    role: 'standard',
    document: () => standardTemplateDocument(),
  },
  {
    id: 'press-clips-compact',
    name: 'Press clips, compact',
    description:
      'The same clip list at 13px throughout, for reports long enough that the default reads as sprawling.',
    legacy: 'Delaware and sunrise-project — 9 subscriptions',
    document: () => {
      const presentation = pressClipsPresentation()
      // `* { font-size: 10pt }` in both. 10pt is 13.33px and the model takes whole px, so this is
      // 13px — a third of a pixel short, and the closest the size control can say.
      const compact = 13
      return documentWith({
        ...presentation,
        styles: {
          ...presentation.styles,
          base: { sizePx: compact },
          headerName: { ...presentation.styles.headerName, sizePx: 16 },
          subHeaderName: { ...presentation.styles.subHeaderName, sizePx: compact },
          meta: { ...presentation.styles.meta, sizePx: compact },
          snippet: { ...presentation.styles.snippet, sizePx: compact },
          headline: { sizePx: compact },
          readMore: { sizePx: compact },
        },
      })
    },
  },
  {
    id: 'bulleted-digest',
    name: 'Bulleted digest',
    description:
      'Every article a bullet rather than a stacked block — scannable when a header carries many short items.',
    legacy: 'utah-template — 8 subscriptions, the largest non-default',
    document: () => {
      const presentation = pressClipsPresentation()
      return documentWith({
        ...presentation,
        articleLayout: 'bullets',
        // The bullet already carries the hierarchy the indents were doing, and keeping both puts
        // the text a long way from its own marker.
        styles: {
          ...presentation.styles,
          meta: { italic: true },
          snippet: { indentPx: 0 },
        },
      })
    },
  },
  {
    id: 'press-clips-plain-headers',
    name: 'Press clips, plain headers',
    description:
      'The standard clip list with unadorned header names, and social posts set in italic.',
    legacy: 'okla-uhg — 4 subscriptions',
    document: () => {
      const presentation = pressClipsPresentation()
      return documentWith({
        ...presentation,
        styles: {
          ...presentation.styles,
          // The one thing this template is: `.header-name` without the underline.
          headerName: { bold: true, underline: false },
        },
        stylesBySource: {
          ...presentation.stylesBySource,
          x: { headline: { bold: true }, base: { italic: true } },
        },
      })
    },
  },
  {
    id: 'the-trade-desk',
    name: 'The Trade Desk',
    description:
      'A designed email rather than a clip list: its own header, a 600px flat card, sentiment chips and a Read More button.',
    legacy: 'emerald — 2 subscriptions, and one of only two designed templates',
    // Brings its own palette, so it is one client's and not a shape to reuse.
    client: 'The Trade Desk',
    document: ttdTemplateDocument,
  },
]

/**
 * The standard layout — what a report with no template renders, and what a new template starts
 * from (CLI-793). It is the legacy portal's `default/` as it was actually sent, because that is the
 * email 256 of the enabled subscriptions' clients have been receiving for years, and "None —
 * standard layout" meaning anything else made every untemplated report a visible redesign.
 *
 * Not `defaultTemplateDocument()`: that one stays the renderer's neutral baseline, which the
 * renderer's own tests are written against.
 */
export function standardTemplateDocument(): BlockTemplateDocument {
  return documentWith(legacyDefaultPresentation(), { legacy: true })
}

export function starterTemplate(id: string): StarterTemplate | undefined {
  return STARTER_TEMPLATES.find((starter) => starter.id === id)
}

/**
 * What installing the starters did.
 *
 * `skipped` rather than an error, because installing is idempotent by design: the point is "make
 * sure these exist", and a name already taken means the job is done for that one. A starter that
 * somebody has since edited or renamed is theirs — this never overwrites a row.
 */
export const installStartersResponseSchema = z.object({
  created: z.array(reportTemplateSummarySchema),
  skipped: z.array(z.string()),
})
export type InstallStartersResponse = z.infer<typeof installStartersResponseSchema>
